import { useEffect, useRef, useState } from 'react'
import { ExternalLink, Pencil, Replace, Trash2, Upload } from 'lucide-react'
import { Button, Drawer, toast } from '@/components/ui'
import { odooUrl } from '@/lib/utils/odoo-url'
import type { CofaAttachment, CofaKind, DocumentRow } from '../api/types'
import { documentUrl } from '../utils/document-url'
import { ChangeProductDrawer } from './ChangeProductDrawer'
import { CustomerCofaCheck } from './CustomerCofaCheck'
import { checkTarget, customerCofa } from '../utils/customer-cofa'
import { DocumentStatusChip } from './DocumentStatusChip'
import {
  useRemoveCofa,
  useRenameCofa,
  useReplaceCofa,
  useUnverifyCofa,
  useVerifyCofa,
} from '../api/queries'

type Tab = 'vendor' | 'spi'

export interface CofaPreviewDrawerProps {
  row: DocumentRow | null
  canAct: boolean
  onClose: () => void
  onPrev?: () => void
  onNext?: () => void
  /** Tab the drawer should open to when `row` changes. Page-level
   *  state drives this — either the page's current Vendor/SPI source
   *  filter or the column the user clicked in the table. The drawer
   *  still allows local tab switching after open. Defaults to
   *  `'vendor'` for backward-compat with existing call sites. */
  initialTab?: Tab
  /** Regulatory/admin: may mark a flagged CofA as verified (or revoke).
   *  Separate from `canAct` because operations can manage files but not
   *  vouch for a certificate's identity. */
  canVerify?: boolean
}

export function CofaPreviewDrawer({
  row,
  canAct,
  onClose,
  onPrev,
  onNext,
  initialTab = 'vendor',
  canVerify = false,
}: CofaPreviewDrawerProps) {
  const [tab, setTab] = useState<Tab>(initialTab)
  // 2026-05-01 — Change Product reassignment (sub-drawer).
  const [changeProductOpen, setChangeProductOpen] = useState(false)
  // Reset the local tab whenever the page picks a new (row, kind)
  // pair. Keying off lot_id + initialTab covers both flows: clicking a
  // different row, and clicking a different column on the same row.
  const lotId = row?.lot_id ?? null
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs tab when row/initialTab change; deps gate prevents loops.
    setTab(initialTab)
  }, [lotId, initialTab])
  const rename = useRenameCofa()
  const remove = useRemoveCofa()
  const replace = useReplaceCofa()
  const verify = useVerifyCofa()
  const unverify = useUnverifyCofa()
  const replaceInputRef = useRef<HTMLInputElement>(null)
  const [pendingKind, setPendingKind] = useState<CofaKind | null>(null)

  const active: CofaAttachment | null = row
    ? tab === 'vendor'
      ? row.vendor_cofa
      : row.spi_cofa
    : null
  const activeKind: CofaKind = tab === 'vendor' ? 'vendor_cofa' : 'spi_cofa'

  function handleRename() {
    if (!row || !active) return
    const next = window.prompt('Rename CofA', active.filename)
    if (!next || next === active.filename) return
    rename.mutate(
      { lotId: row.lot_id, kind: activeKind, newFilename: next },
      {
        onSuccess: () => toast.success(`Renamed ${active.filename}`),
        onError: (err) =>
          toast.danger(`Rename failed${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
  }

  function handleRemove() {
    if (!row || !active) return
    const ok = window.confirm(
      `Remove ${activeKind === 'vendor_cofa' ? 'vendor' : 'SPI'} CofA from ${row.lot_name}? This clears the attachment but keeps the document in the middleware library.`,
    )
    if (!ok) return
    remove.mutate(
      { lotId: row.lot_id, kind: activeKind },
      {
        onSuccess: () => toast.success(`Removed ${activeKind} from ${row.lot_name}`),
        onError: (err) =>
          toast.danger(`Remove failed${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
  }

  function openReplacePicker(kind: CofaKind) {
    setPendingKind(kind)
    replaceInputRef.current?.click()
  }

  function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    const kind = pendingKind
    e.target.value = ''
    setPendingKind(null)
    if (!row || !file || !kind) return
    replace.mutate(
      { file, lotId: row.lot_id, kind },
      {
        onSuccess: (data: unknown) => {
          toast.success(`Replaced ${kind} for ${row.lot_name}`)
          if (
            data &&
            typeof data === 'object' &&
            (data as { spi_cofa_pending?: boolean }).spi_cofa_pending
          ) {
            toast.info('SPI CofA regeneration dispatched')
          }
        },
        onError: (err) =>
          toast.danger(`Replace failed${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
  }

  // Verify / revoke / replace from the check panel always act on the
  // customer CofA (SPI CofA for SPI products), whichever tab is open.
  function handleVerify() {
    if (!row) return
    const target = checkTarget(row)
    const att = target.attachment
    if (!att) return
    const ok = window.confirm(
      `Confirm you have looked at this ${target.label} and it shows lot ${row.lot_name} for ${row.product_name}.\n\nYour name and today's date are recorded, and deliveries of this lot can then ship with this certificate.`,
    )
    if (!ok) return
    const note = window.prompt('Note (optional) — e.g. "lot printed as image, checked visually"') ?? undefined
    verify.mutate(
      { lotId: row.lot_id, kind: target.kind, documentId: att.document_id, note },
      {
        onSuccess: () => toast.success(`${target.label} verified for ${row.lot_name}`),
        onError: (err) =>
          toast.danger(`Verify failed${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
  }

  function handleUnverify() {
    if (!row) return
    const target = checkTarget(row)
    if (!window.confirm(`Remove the verification on ${row.lot_name}? Deliveries of this lot will be blocked again until the ${target.label} is fixed or re-verified.`)) return
    unverify.mutate(
      { lotId: row.lot_id, kind: target.kind },
      {
        onSuccess: () => toast.success(`Verification removed for ${row.lot_name}`),
        onError: (err) =>
          toast.danger(`Revoke failed${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
  }

  const mutationPending =
    rename.isPending || remove.isPending || replace.isPending || verify.isPending || unverify.isPending

  return (
    <Drawer
      open={row !== null}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      size="wide"
      ident={row ? `${row.lot_name} · ${row.vendor_name || '(no vendor)'}` : undefined}
      title={row?.product_name ?? ''}
      meta={
        row ? (
          <>
            <span>Mfg {row.manufacture_date ?? '—'}</span>
            <span className="opacity-40">·</span>
            <span>Exp {row.expiration_date ?? '—'}</span>
            <span className="opacity-40">·</span>
            <DocumentStatusChip status={row.status} />
          </>
        ) : undefined
      }
      onPrev={onPrev}
      onNext={onNext}
      headerActions={
        row ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              window.open(odooUrl('product.product', row.product_id), '_blank')
            }
          >
            Odoo
            <ExternalLink size={12} />
          </Button>
        ) : undefined
      }
      footer={
        row ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            <span className="flex-1" />
            {active ? (
              <a
                href={documentUrl(active.document_id, active.filename)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-sm border border-border-default bg-surface px-2.5 py-1 text-sm text-link hover:bg-surface-hover"
              >
                Download
                <ExternalLink size={12} />
              </a>
            ) : null}
            {canAct ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => setChangeProductOpen(true)}
                  disabled={mutationPending}
                  title="Reassign this CofA to a different SPI product"
                >
                  <Replace size={12} /> Change Product
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleRename}
                  disabled={!active || mutationPending}
                >
                  <Pencil size={12} /> Rename
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => openReplacePicker(activeKind)}
                  disabled={mutationPending}
                >
                  <Upload size={12} /> {active ? 'Replace' : 'Upload'}
                </Button>
                {active ? (
                  <Button
                    variant="danger"
                    onClick={handleRemove}
                    disabled={mutationPending}
                  >
                    <Trash2 size={12} /> Remove
                  </Button>
                ) : null}
                <input
                  ref={replaceInputRef}
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={onFileChosen}
                />
              </>
            ) : null}
          </>
        ) : undefined
      }
    >
      {row ? (
        <DrawerBody
          row={row}
          tab={tab}
          onTabChange={setTab}
          active={active}
          check={
            <CustomerCofaCheck
              row={row}
              viewingCustomerCofa={tab === checkTarget(row).tab}
              canVerify={canVerify}
              canAct={canAct}
              pending={mutationPending}
              onView={() => setTab(checkTarget(row).tab)}
              onVerify={handleVerify}
              onUnverify={handleUnverify}
              onReplace={() => openReplacePicker(checkTarget(row).kind)}
            />
          }
        />
      ) : null}
      {/* Lazy-mount: the reassign hooks shouldn't fire on every preview open.
          Only mount when the user has clicked Change Product. */}
      {row && changeProductOpen ? (
        <ChangeProductDrawer
          open={changeProductOpen}
          onOpenChange={setChangeProductOpen}
          lotId={row.lot_id}
          lotName={row.lot_name}
          vendorId={row.vendor_id}
          vendorName={row.vendor_name}
          currentProductId={row.product_id}
          currentProductName={row.product_name}
        />
      ) : null}
    </Drawer>
  )
}

function DrawerBody({
  row,
  tab,
  onTabChange,
  active,
  check,
}: {
  row: DocumentRow
  tab: Tab
  onTabChange: (tab: Tab) => void
  active: CofaAttachment | null
  check: React.ReactNode
}) {
  const customer = customerCofa(row)
  const otherLabel = customer.tab === 'spi' ? 'vendor CofA' : 'SPI CofA'
  return (
    <div className="flex h-full flex-col gap-4">
      {check}
      <section>
        <SectionLabel>Details</SectionLabel>
        <KvGrid
          rows={[
            ['Lot', <OdooLink key="lot" model="stock.lot" id={row.lot_id} label={row.lot_name} />],
            ['Product', <OdooLink key="prod" model="product.product" id={row.product_id} label={row.product_name} />],
            [
              'Vendor',
              row.vendor_id ? (
                <OdooLink
                  key="vend"
                  model="res.partner"
                  id={row.vendor_id}
                  label={row.vendor_name || '(no vendor)'}
                />
              ) : (
                row.vendor_name || '(no vendor)'
              ),
            ],
            ['Mfg date', row.manufacture_date ?? '—'],
            ['Exp date', row.expiration_date ?? '—'],
            ['Is SPI product', row.is_spi_product ? 'Yes' : 'No'],
          ]}
        />
      </section>

      <section className="flex min-h-0 flex-1 flex-col">
        <div className="mb-2 flex items-center gap-1 border-b border-border-subtle">
          <TabButton active={tab === 'vendor'} onClick={() => onTabChange('vendor')}>
            Vendor CofA {row.vendor_cofa ? '' : '—'}
            {customer.tab === 'vendor' ? <CustomerBadge /> : null}
          </TabButton>
          <TabButton active={tab === 'spi'} onClick={() => onTabChange('spi')}>
            SPI CofA {row.spi_cofa ? '' : '—'}
            {customer.tab === 'spi' ? <CustomerBadge /> : null}
          </TabButton>
        </div>
        {tab !== customer.tab ? (
          <p className="mb-2 text-sm text-secondary" data-testid="not-customer-cofa-note">
            {customer.tab === 'spi'
              ? `For SPI products customers receive the SPI CofA. This vendor CofA isn’t sent to customers, but the SPI CofA’s results come from it, so it must be this lot’s certificate.`
              : `Customers receive the vendor CofA for this product. This ${otherLabel} is not sent to customers.`}
          </p>
        ) : null}
        {active ? (
          <iframe
            key={active.document_id}
            title={active.filename}
            src={documentUrl(active.document_id, active.filename)}
            className="min-h-[60vh] w-full rounded-sm border border-border-subtle bg-surface"
          />
        ) : (
          <div className="flex min-h-[220px] flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-border-default bg-surface-sunk p-4 text-center">
            <span className="text-role-section-label">
              {tab === 'vendor' ? 'Vendor CofA' : 'SPI CofA'}
            </span>
            <span className="text-sm text-secondary">No file on record yet.</span>
          </div>
        )}
      </section>
    </div>
  )
}

function CustomerBadge() {
  return (
    <span className="ml-1.5 rounded-sm bg-accent-bg px-1 py-px text-[10px] font-semibold tracking-wide text-accent">
      SENT TO CUSTOMERS
    </span>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-active={active || undefined}
      className="text-role-section-label inline-flex cursor-pointer items-center border-b-2 border-transparent bg-transparent px-2.5 py-1.5 hover:text-primary data-[active]:border-accent data-[active]:text-primary"
    >
      {children}
    </button>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-role-section-label mb-2 border-b border-border-subtle pb-1">
      {children}
    </div>
  )
}

function KvGrid({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-1 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-tertiary">{k}</dt>
          <dd className="text-primary">{v}</dd>
        </div>
      ))}
    </dl>
  )
}

function OdooLink({
  model,
  id,
  label,
}: {
  model: string
  id: number
  label: string | null | undefined
}) {
  if (!label) return <span className="text-tertiary">—</span>
  return (
    <a
      href={odooUrl(model, id)}
      target="_blank"
      rel="noreferrer"
      className="text-link hover:underline"
    >
      {label}
    </a>
  )
}
